import {
  deleteRequest,
  getRequest,
  getRequestWithAuthorization,
  postRequest,
  putRequest,
} from "./api";
import { API_URLS } from "./appUrls";

import { APP_SCOPE, CIP_SOURCE_OPTIONS } from "../constants/global.constants";

import { extractPlantId } from "../utils/Utils";

const getPlantApiUrl = () => `${API_URLS.plants}/${extractPlantId()}`;
const FORCE_CA_SCOPE = { overrideAppScope: APP_SCOPE.CA };
const IGNORE_APP_SCOPE = { overrideAppScope: APP_SCOPE.BASE };

export const getToken = (header, i18n) =>
  getRequestWithAuthorization(`${API_URLS.token}`, header, i18n);

export const accessRequest = (payload) =>
  postRequest(`${API_URLS.user}/${API_URLS.accessRequest}`, payload);

export const acceptTC = () => postRequest(`${API_URLS.acceptTC}`);

export const uploadSelectedFile = (
  fileData,
  chunkIndex,
  totalChunks,
  selectedTrain,
  commitBlockList,
  plantFileId,
) => {
  const queryParams = new URLSearchParams({
    chunkIndex,
    totalChunks,
    train_num: selectedTrain,
    commit_block_list: commitBlockList,
  });

  if (plantFileId != null && plantFileId !== "") {
    queryParams.append("plant_file_id", plantFileId);
  }

  return postRequest(
    `${getPlantApiUrl()}/uploadfile?${queryParams.toString()}`,
    fileData,
  );
};
export const uploadSelectedSheet = (selectedTrain, plantFileId, sheetName) =>
  postRequest(
    `${getPlantApiUrl()}/sheet?train_num=${selectedTrain}&plant_file_id=${plantFileId}&sheet_name=${encodeURIComponent(sheetName)}`,
  );

export const deleteUploadedFile = (plantFileId) =>
  deleteRequest(`${getPlantApiUrl()}/files/${plantFileId}`);

export const getLastFileDetails = (selectedTrain, requestConfig = {}) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.details}?train_num=${selectedTrain}`,
    requestConfig,
  );

export const updatePlantInformation = (payload, selectedTrain) =>
  putRequest(
    `${getPlantApiUrl()}/${API_URLS.plantDetails}?train_num=${selectedTrain}`,
    payload,
  );

export const getCIPs = (
  selectedTrain,
  pageSize = 10,
  currentPage = 1,
  source = null,
  historicOnly = false,
) => {
  const baseURL = getPlantApiUrl();

  const formattedSource =
    source === CIP_SOURCE_OPTIONS.USER
      ? Object.values(CIP_SOURCE_OPTIONS)
      : [source];

  const sourceParams = new URLSearchParams();

  formattedSource.forEach((item) => {
    sourceParams.append("source", item);
  });

  const limit = pageSize;
  const offset = (currentPage - 1) * pageSize;

  let url = `${baseURL}/${API_URLS.cips}?train_num=${selectedTrain}&historic_only=${historicOnly}`;
  if (limit && offset) {
    url += `&limit=${limit}&offset=${offset}`;
  }
  if (source) {
    url += `&${sourceParams.toString()}`;
  }

  return getRequest(url, FORCE_CA_SCOPE);
};

export const uploadBulkCIPs = (selectedTrain, payload) => {
  return postRequest(
    `${getPlantApiUrl()}/${API_URLS.cips}/${
      API_URLS.bulk
    }?train_num=${selectedTrain}`,
    payload,
  );
};

export const uploadCips = (payload, selectedTrain) => {
  const baseURL = getPlantApiUrl();

  return postRequest(
    `${baseURL}/${API_URLS.uploadCips}?train_num=${selectedTrain}`,
    payload,
    FORCE_CA_SCOPE,
  );
};

export const uploadRecurrentCips = (
  payload,
  selectedTrain,
  proceedMissingFlag,
) => {
  let baseURL = getPlantApiUrl();

  return postRequest(
    `${baseURL}/${API_URLS.uploadCips}/${API_URLS.recurrent}?train_num=${selectedTrain}&proceed_on_missing=${proceedMissingFlag}`,
    payload,
  );
};

export const editCip = (cipId, payload, selectedTrain) => {
  const baseURL = getPlantApiUrl();

  return putRequest(
    `${baseURL}/${API_URLS.uploadCips}/${cipId}?train_num=${selectedTrain}`,
    payload,
    FORCE_CA_SCOPE,
  );
};

export const deleteCipsBySource = (selectedTrain, source) => {
  const url = `${getPlantApiUrl()}/${
    API_URLS.cips
  }?train_num=${selectedTrain}&cip_source=${source}`;
  return deleteRequest(url);
};

export const deleteCip = (cipId, selectedTrain) => {
  const baseURL = getPlantApiUrl();

  return deleteRequest(
    `${baseURL}/${API_URLS.uploadCips}/${cipId}?train_num=${selectedTrain}`,
    {},
    FORCE_CA_SCOPE,
  );
};

export const cleaningEffectivness = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${
      API_URLS.executeCleaning
    }?recalculate=true&train_num=${selectedTrain}`,
  );

export const cleaningEffectivnessStatus = (task_id, selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${
      API_URLS.executeCleaningStatus
    }?run_id=${task_id}&train_num=${selectedTrain}`,
  );

export const cleanedData = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.cleanedData}?train_num=${selectedTrain}`,
    FORCE_CA_SCOPE,
  );

export const rerunCIPs = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.cips}/${
      API_URLS.rerun
    }?train_num=${selectedTrain}`,
  );

export const getFoulingInformation = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.fouling}?train_num=${selectedTrain}`,
  );

export const getPredictLimits = (selectedTrain) => {
  return getRequest(
    `${getPlantApiUrl()}/limits?train_num=${selectedTrain}`,
    FORCE_CA_SCOPE,
  );
};

export const predictionCalculation = (
  window,
  selectedTrain,
  proceedMissingFlag = false,
) =>
  getRequest(
    `${getPlantApiUrl()}/prediction/calculation?forecast_window=${window}&train_num=${selectedTrain}&proceed_on_missing_cips=${proceedMissingFlag}`,
  );

export const getPredictionData = (selectedTrain, forecastWindow) =>
  getRequest(
    `${getPlantApiUrl()}/prediction?train_num=${selectedTrain}&forecast_window=${forecastWindow}`,
  );

export const getPredictionStatus = (task_id) =>
  getRequest(`${getPlantApiUrl()}/prediction/status?run_id=${task_id}`);

export const getHeaders = () =>
  getRequest(`${getPlantApiUrl()}/${API_URLS.header}`);

export const getfoulingType = () =>
  getRequest(`${getPlantApiUrl()}/${API_URLS.foulingType}`);

export const getAnalysisJob = (
  forecast_window,
  selectedTrain,
  recommendedCipsFlag = 0,
) =>
  getRequest(
    `${getPlantApiUrl()}/${
      API_URLS.analysisjob
    }?forecast_window=${forecast_window}&train_num=${selectedTrain}&recommended_cips_flag=${recommendedCipsFlag}`,
  );

export const getAnalysisStatus = (task_id) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.analysisStatus}?run_id=${task_id}`,
  );

export const getAnalysis = (
  selectedTrain,
  forecastWindow,
  recommendedCipsFlag = 0,
) =>
  getRequest(
    `${getPlantApiUrl()}/${
      API_URLS.analysis
    }?train_num=${selectedTrain}&forecast_window=${forecastWindow}&recommended_cips_flag=${recommendedCipsFlag}`,
  );

export const getType = () => getRequest(`${getPlantApiUrl()}/${API_URLS.type}`);

export const getUserChemicalLibrary = () => {
  let url = `${API_URLS.user}/${API_URLS.chemicalLibrary}`;
  return getRequest(url, IGNORE_APP_SCOPE);
};

export const getUserChemicalUsage = (chemId) => {
  const url = `${API_URLS.user}/${API_URLS.chemicalLibrary}/${chemId}/${API_URLS.chemicalUsage}`;
  return getRequest(url, IGNORE_APP_SCOPE);
};

export const updateUserChemicalLibrary = (payload) => {
  let url = `${API_URLS.user}/${API_URLS.chemicalLibrary}`;
  return putRequest(url, payload, IGNORE_APP_SCOPE);
};

export const deleteUserChemicalLibrary = (payload) => {
  let url = `${API_URLS.user}/${API_URLS.chemicalLibrary}`;
  return deleteRequest(url, payload, IGNORE_APP_SCOPE);
};

export const addCustomChemicalToUserLibrary = (payload) => {
  let url = `${API_URLS.user}/${API_URLS.chemicalLibrary}`;
  return postRequest(url, payload, IGNORE_APP_SCOPE);
};

export const getPlantChemicalLibrary = () => {
  let url = `${getPlantApiUrl()}/${API_URLS.chemicalLibrary}`;
  return getRequest(url, IGNORE_APP_SCOPE);
};

export const updatePlantChemicalLibrary = (payload) => {
  let url = `${getPlantApiUrl()}/${API_URLS.chemicalLibrary}`;
  return putRequest(url, payload, IGNORE_APP_SCOPE);
};

export const getUserUtilityCosts = () => {
  let url = `${API_URLS.user}/${API_URLS.utilityCosts}`;
  return getRequest(url);
};

export const updateUserUtilityCosts = (payload) => {
  let url = `${API_URLS.user}/${API_URLS.utilityCosts}`;
  return putRequest(url, payload);
};

export const getPlantUtilityCosts = () => {
  let url = `${getPlantApiUrl()}/${API_URLS.utilityCosts}`;
  return getRequest(url);
};

export const updatePlantUtilityCosts = (payload) => {
  let url = `${getPlantApiUrl()}/${API_URLS.utilityCosts}`;
  return putRequest(url, payload);
};

export const completeFeatureTour = (payload) =>
  putRequest(`${API_URLS.user}/${API_URLS.tour}`, payload);

export const getPlants = () =>
  getRequest(`${API_URLS.plants}`, IGNORE_APP_SCOPE);

export const getPlantDetails = (plantId, includeFile = false, selectedTrain) =>
  getRequest(
    `${API_URLS.plants}/${plantId}?includeFile=${includeFile}&sel_train=${selectedTrain}`,
  );

export const getFileSheetMapping = (plantId) =>
  getRequest(`${API_URLS.plants}/${plantId}/${API_URLS.fileSheetMapping}`);

export const updateFileSheetMapping = (plantId, payload) =>
  postRequest(
    `${API_URLS.plants}/${plantId}/${API_URLS.fileSheetMapping}`,
    payload,
  );

export const deletePlants = () => deleteRequest(`${getPlantApiUrl()}`);

export const savePlant = (payload) =>
  postRequest(`${API_URLS.plants}`, payload);

export const sharePlant = (plant_id, payload) =>
  postRequest(`${API_URLS.plants}/${plant_id}/${API_URLS.share}`, payload);

export const unsharePlant = (plant_id, email) =>
  deleteRequest(
    `${API_URLS.plants}/${plant_id}/${API_URLS.share}?shared_email=${email}`,
  );

export const getUsers = (plant_id) =>
  getRequest(`${API_URLS.plants}/${plant_id}/${API_URLS.users}`);

export const editPlant = (plantId, payload) =>
  putRequest(`${API_URLS.plants}/${plantId}`, payload);

export const getSheetColumns = (fileId, selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.cols}/${fileId}?train_num=${selectedTrain}`,
  );

export const getColumnMapping = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.colmapping}?train_num=${selectedTrain}`,
  );

export const updateColumnMapping = (fileId, payload, selectedTrain) =>
  putRequest(
    `${getPlantApiUrl()}/${
      API_URLS.colmapping
    }/${fileId}?train_num=${selectedTrain}`,
    payload,
  );

export const fetchAutoDetectColumns = (columnOptions, selectedTrain) => {
  const payload = {
    headers: columnOptions,
    train_num: selectedTrain,
  };
  return postRequest(`${API_URLS.plants}/${API_URLS.columnDetect}`, payload);
};

export const getReference = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.refcondition}?train_num=${selectedTrain}`,
  );

export const confirmRefrenceCondition = (fileId, payload, selectedTrain) =>
  putRequest(
    `${getPlantApiUrl()}/${
      API_URLS.refcondition
    }/${fileId}?train_num=${selectedTrain}`,
    payload,
  );

export const getDataVisualization = (fileId, selectedTrain, appendId) => {
  let url = `${getPlantApiUrl()}/files/${fileId}/${
    API_URLS.visualization
  }?train_num=${selectedTrain}`;
  if (appendId) {
    url += `&append_id=${appendId}`;
  }
  return getRequest(url);
};

export const setadvanceSettings = (payload, selectedTrain) =>
  putRequest(
    `${getPlantApiUrl()}/${API_URLS.advancesettings}?train_num=${selectedTrain}`,
    payload,
    IGNORE_APP_SCOPE,
  );

export const getadvanceSettings = (selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/${API_URLS.advancesettings}?train_num=${selectedTrain}`,
    IGNORE_APP_SCOPE,
  );

export const getPlantReadiness = () =>
  getRequest(`${getPlantApiUrl()}/${API_URLS.readiness}`);

export const getProceedToAnalysis = (fileId, selectedTrain) =>
  getRequest(
    `${getPlantApiUrl()}/files/${fileId}/${
      API_URLS.proceedToAnalysis
    }?train_num=${selectedTrain}`,
    IGNORE_APP_SCOPE,
  );

export const setState = (state, selectedTrain) =>
  putRequest(
    `${getPlantApiUrl()}/${
      API_URLS.state
    }?state=${state}&train_num=${selectedTrain}`,
  );

export const saveUser = (payload) =>
  postRequest(`${API_URLS.user}`, payload, IGNORE_APP_SCOPE);

export const getAccessRequest = (
  page = 1,
  pageSize = 5,
  filters,
  sortField,
  sortOrder,
) => {
  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    sort_by: sortField,
    order: sortOrder,
    limit: pageSize,
    offset,
  });

  if (filters && filters.length > 0) {
    filters.forEach((f) => params.append("filters", f));
  }

  const url = `${API_URLS.admin}/${
    API_URLS.accessRequests
  }?${params.toString()}`;
  return getRequest(url);
};

export const updateRequests = (payload) =>
  putRequest(`${API_URLS.admin}/${API_URLS.accessRequests}`, payload);

export const getAllUsers = (
  page = 1,
  pageSize = 5,
  filters,
  sortField,
  sortOrder,
) => {
  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    sort_by: sortField,
    order: sortOrder,
    limit: pageSize,
    offset,
  });

  if (filters && filters.length > 0) {
    filters.forEach((f) => params.append("filters", f));
  }

  const url = `${API_URLS.admin}/${API_URLS.users}?${params.toString()}`;
  return getRequest(url);
};

export const getAllPlantsForUser = (userEmail) => {
  const url = `${API_URLS.admin}/user-plants?user_email=${encodeURIComponent(
    userEmail,
  )}`;
  return getRequest(url);
};

export const exportExcel = (type) => {
  const url = `${API_URLS.admin}/${API_URLS.exportExcel}?type=${encodeURIComponent(type)}`;
  // Request as blob for Excel download
  return getRequest(url, { responseType: "blob" });
};

export const downloadDatasetFile = (selectedTrain) => {
  const url = `${getPlantApiUrl()}/${
    API_URLS.downloadFile
  }/file?train_num=${selectedTrain}`;
  // Request as blob for file download
  return getRequest(url, { responseType: "blob" });
};

export const getDefaultCIPValues = (selectedTrain) =>
  getRequest(`${getPlantApiUrl()}/cips/defaults?train_num=${selectedTrain}`);

export const editDefaultCIPValues = (selectedTrain, data) =>
  putRequest(
    `${getPlantApiUrl()}/cips/defaults?train_num=${selectedTrain}`,
    data,
  );

export const uploadAppendFile = (
  fileData,
  chunkIndex,
  totalChunks,
  selectedTrain,
  commitBlockList,
  appendId = null,
) => {
  let url = `${getPlantApiUrl()}/${API_URLS.upload}/${
    API_URLS.appendFile
  }?train_num=${selectedTrain}&chunkIndex=${chunkIndex}&totalChunks=${totalChunks}&commit_block_list=${commitBlockList}`;
  if (appendId) {
    url += `&append_id=${appendId}`;
  }
  return postRequest(url, fileData);
};

export const appendExistingFile = (
  selectedTrain,
  appendId,
  proceed_on_warning = false,
) =>
  postRequest(
    `${getPlantApiUrl()}/${
      API_URLS.appendFile
    }?train_num=${selectedTrain}&append_id=${appendId}&proceed_on_warning=${proceed_on_warning}`,
  );

export const deleteAppendFile = (selectedTrain, appendId) =>
  deleteRequest(
    `${getPlantApiUrl()}/${
      API_URLS.appendFile
    }?train_num=${selectedTrain}&append_id=${appendId}`,
  );

export const getWordReport = (trainNum) => {
  const url = `${getPlantApiUrl()}/${API_URLS.report}?train_num=${trainNum}`;
  return getRequest(url, { responseType: "blob" });
};
export const getLifetimeWordReport = (trainNum, payload = {}) => {
  const url = `${getPlantApiUrl()}/${API_URLS.report}?train_num=${trainNum}`;
  return postRequest(url, payload, { responseType: "blob" });
};

export const getRONFElements = (waterType) => {
  let url = `${API_URLS.plants}/${API_URLS.roElements}`;
  if (waterType) {
    url += `?feed_water_type=${waterType}`;
  }
  return getRequest(url);
};

export const getPlantsAvailableForBenchmark = () => {
  return getRequest(
    `${API_URLS.plants}/${API_URLS.benchmarking}/${API_URLS.availability}`,
  );
};

export const getBenchmarkingData = (payload) => {
  return postRequest(
    `${API_URLS.plants}/${API_URLS.benchmarking}/${API_URLS.data}`,
    payload,
  );
};

export const extractWaveProConfig = (file) => {
  return postRequest(
    `${API_URLS.plants}/${API_URLS.wave}/${API_URLS.upload}`,
    file,
  );
};
